import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Gift, Eye, EyeSlash, Warning } from "@phosphor-icons/react";

/**
 * Super-Admin · Promos
 * ────────────────────
 * Single global kill-switch that controls whether the golden
 * "Anniversary 50% Cashback" banner renders inside every seeker's
 * chat window (both active + post-end states). The ANNIVERSARY50
 * coupon itself remains technically valid even when the banner is
 * OFF — users who already know the code can still type it in the
 * wallet modal — but no in-chat marketing surface is shown.
 *
 * Backend:
 *   GET  /api/super-admin/promos/anniversary  → {enabled, updated_at, updated_by}
 *   POST /api/super-admin/promos/anniversary  {enabled: bool}
 *   GET  /api/promos/anniversary              → {enabled} (public — read by ChatPage)
 */
export default function AdminPromosPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  useEffect(() => {
    // Wait for AuthContext to finish hydrating /api/auth/me before deciding
    // to redirect — otherwise the initial `user === null` state during the
    // first render kicks every visitor (including super-admins) back to /.
    if (authLoading) return;
    if (!user || !user.is_super_admin) navigate("/");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    // Only fetch after auth has resolved AND the visitor is a super-admin.
    // Avoids the brief 403 + error toast that fires while auth is still in
    // flight (or for non-admins who'll be redirected away anyway).
    if (authLoading) return;
    if (!user || !user.is_super_admin) return;
    (async () => {
      try {
        const { data } = await api.get("/super-admin/promos/anniversary");
        setEnabled(!!data.enabled);
        setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
      } catch {
        toast.error("Couldn't load promo settings");
      } finally {
        setLoading(false);
      }
    })();
  }, [authLoading, user]);

  const toggle = async () => {
    const next = !enabled;
    setSaving(true);
    try {
      const { data } = await api.post("/super-admin/promos/anniversary", { enabled: next });
      setEnabled(!!data.enabled);
      toast.success(
        next
          ? "Anniversary banner is now LIVE in every chat."
          : "Anniversary banner is now hidden in every chat."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <PageShell>
        <div className="max-w-2xl mx-auto py-12 px-4 text-muted" data-testid="admin-promos-loading">
          Loading…
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="max-w-2xl mx-auto py-12 px-4" data-testid="admin-promos-page">
        <div className="flex items-center gap-3 mb-2">
          <Gift size={28} weight="duotone" className="text-amber-500" />
          <h1 className="font-serif-display text-3xl sm:text-4xl font-light">In-Chat Promos</h1>
        </div>
        <p className="text-sm text-muted mb-8">
          Toggle the marketing ribbons that appear inside the seeker chat
          window. Changes apply within seconds — no deploy required.
        </p>

        <div className="card-surface rounded-xl p-5 sm:p-6 border border-amber-200/50">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-lg">💰</span>
                <h2 className="font-semibold text-base">Anniversary 50% Cashback banner</h2>
              </div>
              <p className="text-xs sm:text-sm text-muted mt-1.5 leading-relaxed">
                Golden ribbon at the top of every seeker chat. While the chat
                is live it teases <i>&ldquo;Anniversary special &middot; You WON something
                special&rdquo;</i>. The moment the chat ends, it morphs into{" "}
                <b>&ldquo;You WON 50% Cashback &middot; Recharge now to avail&rdquo;</b> with a
                green Recharge CTA that opens the wallet modal with{" "}
                <code className="px-1 py-0.5 bg-amber-50 rounded text-[11px]">ANNIVERSARY50</code>{" "}
                auto-applied.
              </p>
              <p className="text-[11px] text-muted mt-2">
                {enabled ? (
                  <span className="text-emerald-700 flex items-center gap-1">
                    <Eye size={12} weight="duotone" /> Currently visible to all users.
                  </span>
                ) : (
                  <span className="text-rose-700 flex items-center gap-1">
                    <EyeSlash size={12} weight="duotone" /> Currently hidden — chats look normal.
                  </span>
                )}
              </p>
            </div>

            <button
              type="button"
              onClick={toggle}
              disabled={saving}
              role="switch"
              aria-checked={enabled}
              data-testid="anniversary-promo-toggle"
              className={`flex-shrink-0 relative inline-flex h-7 w-12 items-center rounded-full transition ${
                enabled ? "bg-emerald-600" : "bg-stone-400"
              } ${saving ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                  enabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {!enabled && (
            <div className="mt-4 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-900 flex items-start gap-2" data-testid="anniversary-promo-off-note">
              <Warning size={14} weight="duotone" className="flex-shrink-0 mt-0.5" />
              <span>
                Fully reverted to a normal recharge flow: the in-chat banner
                is hidden, the auto-applied CTA is gone, AND the
                ANNIVERSARY50 coupon itself is rejected at checkout (so a
                user who knows the code can&rsquo;t bypass the switch).
                Payments go through at recharge + 18% GST with no cashback.
                Flip back ON to restore everything instantly.
              </span>
            </div>
          )}

          {meta.updated_at && (
            <p className="text-[11px] text-muted mt-3" data-testid="anniversary-promo-meta">
              Last changed by <b>{meta.updated_by || "—"}</b> ·{" "}
              {new Date(meta.updated_at).toLocaleString()}
            </p>
          )}
        </div>
      </div>
    </PageShell>
  );
}
